import type { EntryCollection, EntrySkeletonType } from 'contentful'

export const usePreviewContentPageFilter = () => {
  const { getPanelInfo } = useScheduledActions()

  const getLocaleKey = (locale: string): string => `pages_${locale.replace('-', '_')}`

  const transformContent = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const firstItemContentType = entries.items?.[0]?.sys?.contentType?.sys?.id
    if (firstItemContentType !== 'contentPage') return entries

    const transformedEntries = {
      ...entries,
      items: entries.items.map((item) => {
        const itemContentType = item?.sys?.contentType?.sys?.id
        if (itemContentType !== 'contentPage') return item

        const localeKey = getLocaleKey(item?.sys?.locale || 'en-AU')
        const itemFields = (item.fields || {}) as Record<string, any>
        const { label, ...contentFields } = itemFields

        return {
          ...item,
          fields: {
            ...(label !== undefined && { label }),
            slug: '/',
            contentType: 'contentPage',
            [localeKey]: [
              {
                ...item,
                fields: contentFields,
              },
            ],
          },
        }
      }),
    }

    return transformedEntries
  }

  const filterByLocale = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const localeKey = getLocaleKey(previewConfig.previewLocale)

    return {
      ...entries,
      items: entries.items.map((item) => {
        const fields = { ...item.fields }
        const localePageKeys = Object.keys(fields).filter((key) => key.startsWith('pages_'))

        localePageKeys.forEach((key) => {
          if (key !== localeKey) {
            delete fields[key]
          }
        })

        return { ...item, fields }
      }),
    }
  }

  const filterByPreviewId = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const localeKey = getLocaleKey(previewConfig.previewLocale)

    return {
      ...entries,
      items: entries.items.map((item) => {
        const fields = { ...item.fields }

        if (previewConfig.previewId && Array.isArray(fields[localeKey])) {
          const localePages = fields[localeKey] as any[]
          const filteredPages = localePages.filter((page) => page.sys.id === previewConfig.previewId)
          fields[localeKey] = filteredPages.length > 0 ? filteredPages : localePages
        }

        return { ...item, fields }
      }),
    }
  }

  const filterByPersonalizationTag = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const localeKey = getLocaleKey(previewConfig.previewLocale)
    const tag = previewConfig.previewPersonalizationTag

    return {
      ...entries,
      items: entries.items.map((item) => {
        const pages = getNestedValue(item, ['fields', `${localeKey}:0`]) || {}
        const panels = (pages.fields?.panels || []) as LocalePageEntry[]

        const filteredPanels = panels.filter((panel) => {
          const tags = (panel.fields as any)?.personalizationTags || []
          return tags.length === 0 || tags.includes(tag)
        })

        return {
          ...item,
          fields: {
            ...item.fields,
            [localeKey]: [
              {
                ...pages,
                fields: {
                  ...pages.fields,
                  panels: filteredPanels,
                },
              },
            ],
          },
        }
      }),
    }
  }

  const getPages = (items, localeKey: string, fallbackKey?: string): LocalePageEntry[] | null => {
    if (!items?.length) return null
    const pages = items[0].fields[localeKey] || (fallbackKey && items[0].fields[fallbackKey])
    return Array.isArray(pages) && pages.length > 0 ? pages : null
  }

  const getPanelsFromPages = (pages: LocalePageEntry[] | null): LocalePageEntry[] => {
    if (!pages?.[0]?.fields) return []
    return (pages[0].fields as PageFields)?.panels || []
  }

  const getAllPanels = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ): PanelInfo[] => {
    const localeKey = getLocaleKey(previewConfig.previewLocale)
    const fallbackKey = localeKey === 'pages_fr_CA' ? 'pages_en_CA' : undefined

    const pageEntries = getPages(entries.items, localeKey, fallbackKey) ?? []
    const nestedPanels = getPanelsFromPages(pageEntries)
    const pageIds = new Set(pageEntries.map((page) => page.sys.id))
    const dedupedEntries = new Map<string, LocalePageEntry>()

    for (const entry of [...pageEntries, ...nestedPanels]) {
      const id = entry?.sys?.id
      if (id && !dedupedEntries.has(id)) dedupedEntries.set(id, entry)
    }

    return [...dedupedEntries.values()].map((entry) => getPanelInfo(entry, { isParentPage: pageIds.has(entry.sys.id) }))
  }

  const removePanelsForDisplayInfo = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    panelDisplayInfo: Record<string, { show: boolean | string }>,
    previewConfig: PreviewConfig,
    options?: { panels?: PanelInfo[]; scheduledActions?: Record<string, any> },
  ) => {
    const { items } = entries
    if (!items?.length) return entries

    const locale = previewConfig.previewLocale || 'en-AU'
    const localeKey = getLocaleKey(locale)
    const fallbackKey = locale === 'fr-CA' ? 'pages_en_CA' : undefined

    if (shouldHidePreviewPayload(previewConfig, panelDisplayInfo, options)) {
      return {
        ...entries,
        items: entries.items.map((item) => ({
          ...item,
          fields: {
            ...item.fields,
            [localeKey]: [],
          },
        })),
      }
    }

    const pages = getPages(items, localeKey, fallbackKey)

    if (!pages?.[0]?.fields) return entries

    const sourcePanels = (pages[0].fields?.panels || []) as LocalePageEntry[]
    pages[0].fields.panels = sourcePanels.filter((panel) => panelDisplayInfo[panel.sys.id]?.show !== false)

    return entries
  }

  const filterByMarket = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterEntriesByMarket(entries, previewConfig.previewMarket as string)

  const filterByPreviewDate = async (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterByPreviewDateOrchestration(entries, previewConfig, getAllPanels, removePanelsForDisplayInfo)

  return {
    transformContent,
    filterByLocale,
    filterByPreviewId,
    filterByPersonalizationTag,
    filterByMarket,
    filterByPreviewDate,
    getAllPanels,
  }
}
